import * as RadixTooltip from "@radix-ui/react-tooltip";
import { useEffect, useRef, useState } from "react";
import { css } from "@pigment-css/react";
import { debounce } from "@acto/web-common/functions";
import { clsx } from "clsx";

export interface TruncateProps {
  children: string | React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  placement?: "top" | "right" | "bottom" | "left";
  lines?: number;
  fullSize?: boolean;
  additionalContent?: React.ReactNode;
  maxWidth?: string;
  customChildren?: React.ReactNode;
}

const tooltipContent = css(({ theme }) => ({
  color: theme.managementView.colors.neutral[90],
  fontFamily: theme.managementView.font.familySecondary,
  backgroundColor: theme.managementView.colors.neutral[0],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[12]}`,
  borderRadius: theme.managementView.radius[16],
  boxSizing: "border-box",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  fontSize: theme.managementView.font.size[14],
  lineHeight: theme.managementView.font.lineHeight[120],
  fontWeight: theme.managementView.font.weight[400],
  maxWidth: 280,
  zIndex: 50,
  wordBreak: "break-word",
}));

const baseTextStyle = css({
  overflow: "hidden",
  wordWrap: "break-word",
  WebkitBoxOrient: "vertical",
  display: "-webkit-box",
  textOverflow: "ellipsis",
});

const oneLineTextStyle = css({
  lineBreak: "anywhere",
});

export const Truncate = ({
  children,
  placement = "bottom",
  lines = 1,
  maxWidth,
  customChildren,
  className,
  style,
}: TruncateProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      const el = ref.current;
      if (!el) return;

      const isClamped = el.scrollHeight > el.offsetHeight + 1;
      setIsOverflowing(isClamped);
    };

    // Added a debounce function in case there are several page resizes within a short time.
    const debouncedCheckOverflow = debounce(checkOverflow, 200);

    debouncedCheckOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [children, lines]);

  return (
    <RadixTooltip.Provider delayDuration={300}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>
          <span
            ref={ref}
            className={clsx(baseTextStyle, className, lines === 1 && oneLineTextStyle)}
            style={{
              WebkitLineClamp: lines,
              maxWidth: maxWidth ?? "100%",
              ...style,
            }}
          >
            {customChildren ?? children}
          </span>
        </RadixTooltip.Trigger>
        {(isOverflowing || customChildren) && (
          <RadixTooltip.Portal>
            <RadixTooltip.Content className={tooltipContent} side={placement} sideOffset={8}>
              {children}
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        )}
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
